import { Radio as RadioAria } from "react-aria-components";
import type { RadioProps } from "react-aria-components";
import type { ReactNode } from "react";

import { cn } from "@/lib/helpers/cn";

type RatingOptionProps = {
  label: ReactNode;
} & Omit<RadioProps, "children">;

export function RatingOption({ className, label, ...radioProps }: RatingOptionProps) {
  return (
    <RadioAria
      className={cn(
        "tw:mb-0! tw:flex! tw:cursor-pointer tw:items-center tw:justify-center",
        "tw:text-center tw:leading-normal tw:font-normal",
        "tw:border",
        "tw:h-16 tw:max-md:px-3 tw:md:px-6",
        "tw:max-md:text-xs tw:md:text-md",

        // responsive border rounding and overlap
        "tw:max-md:rounded-none tw:max-md:first:rounded-l-lg tw:max-md:last:rounded-r-lg tw:md:rounded-lg",
        "tw:max-md:not-first:-ml-px",

        // Default state
        "tw:border-stroke-quaternary tw:bg-fill-white tw:text-text-primary",

        // Selected state
        "tw:selected:border-stroke-teal-primary tw:selected:bg-fill-teal-quinary tw:selected:font-semibold tw:selected:text-text-teal-primary",
        "tw:selected:shadow-[inset_0_0_0_1px_var(--tw-color-stroke-teal-primary)]",
        "tw:selected:relative tw:selected:z-10",

        // Hover state (not selected)
        "tw:hover:not-selected:border-stroke-teal-primary",
        "tw:hover:not-selected:relative tw:hover:not-selected:z-10",

        // Focus state
        "tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2",
        "tw:focus-visible:outline-stroke-bright-purple-secondary",

        // Disabled state
        "tw:disabled:cursor-not-allowed tw:disabled:opacity-50",

        className,
      )}
      {...radioProps}
    >
      {/* Invisible bold text reserves dimensions to prevent layout shift */}
      <div className="tw:relative">
        <span aria-hidden="true" className="tw:pointer-events-none tw:invisible tw:font-semibold">
          {label}
        </span>

        <span className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center">
          {label}
        </span>
      </div>
    </RadioAria>
  );
}
